import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Controller } from 'react-hook-form';

import Input from '../../../common/form/Input';
import RadioButtons from '../../../common/buttons/RadioButtons';
import Autocomplete from '../../../common/form/AutoComplete';
import { FormValues } from './types';
import {
	airlineOptions,
	hotelOptions,
	carRentalOptions,
	rideSharingOptions,
} from './constants';

interface TravelPreferencesStepProps {
	formValues: FormValues;
	setValue: any;
	control: any;
	errors: any;
	isRequired?: boolean;
}

export default function TravelPreferencesStep({
	formValues,
	setValue,
	control,
	errors,
	isRequired = true,
}: TravelPreferencesStepProps) {
	return (
		<Box sx={{ py: 2 }}>
			{/* Preferred Airline Details */}
			<Typography variant="h6" sx={{ mb: 3, color: 'text.label' }}>
				Preferred Airline Details
			</Typography>
			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12}>
					<Controller
						name="preferredSeating"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Preferred seating"
								name="preferredSeating"
								options={[
									{ value: 'window', label: 'Window' },
									{ value: 'middle', label: 'Middle' },
									{ value: 'aisle', label: 'Aisle' },
								]}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="preferredAirlines"
						control={control}
						render={({ field }) => (
							<Autocomplete
								{...field}
								label="Preferred airlines"
								options={airlineOptions}
								multiple
								onChange={(event, newValue) => {
									const values = newValue.map((item) =>
										typeof item === 'string'
											? item
											: item.id
									);
									field.onChange(values);
								}}
								value={
									field.value
										? airlineOptions.filter((option) =>
												field.value.includes(option.id)
										  )
										: []
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="deltaMileage"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Delta Milage #"
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="unitedMileage"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="United Milage #"
								fullWidth
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Hotel Preferences */}
			<Typography variant="h6" sx={{ mb: 3, color: 'text.label' }}>
				Hotel Preferences
			</Typography>
			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12}>
					<Controller
						name="preferredHotels"
						control={control}
						render={({ field }) => (
							<Autocomplete
								{...field}
								label="Preferred hotels"
								options={hotelOptions}
								multiple
								onChange={(event, newValue) => {
									const values = newValue.map((item) =>
										typeof item === 'string'
											? item
											: item.id
									);
									field.onChange(values);
								}}
								value={
									field.value
										? hotelOptions.filter((option) =>
												field.value.includes(option.id)
										  )
										: []
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="hiltonNumber"
						control={control}
						render={({ field }) => (
							<Input {...field} label="Hilton #" fullWidth />
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="marriottBonvoyNumber"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Marriott Bonvoy #"
								fullWidth
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Car Rental Preferences */}
			<Typography variant="h6" sx={{ mb: 3, color: 'text.label' }}>
				Car Rental Preferences
			</Typography>
			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12}>
					<Controller
						name="preferredCarRentals"
						control={control}
						render={({ field }) => (
							<Autocomplete
								{...field}
								label="Preferred car rentals"
								options={carRentalOptions}
								multiple
								onChange={(event, newValue) => {
									const values = newValue.map((item) =>
										typeof item === 'string'
											? item
											: item.id
									);
									field.onChange(values);
								}}
								value={
									field.value
										? carRentalOptions.filter((option) =>
												field.value.includes(option.id)
										  )
										: []
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="avisNumber"
						control={control}
						render={({ field }) => (
							<Input {...field} label="Avis #" fullWidth />
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="budgetNumber"
						control={control}
						render={({ field }) => (
							<Input {...field} label="Budget #" fullWidth />
						)}
					/>
				</Grid>
			</Grid>

			{/* Ride Sharing Preferences */}
			<Typography variant="h6" sx={{ mb: 3, color: 'text.label' }}>
				Ride Sharing Preferences
			</Typography>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Controller
						name="preferredRideSharingApps"
						control={control}
						render={({ field }) => (
							<Autocomplete
								{...field}
								label="Preferred rider sharing apps"
								options={rideSharingOptions}
								multiple
								onChange={(event, newValue) => {
									const values = newValue.map((item) =>
										typeof item === 'string'
											? item
											: item.id
									);
									field.onChange(values);
								}}
								value={
									field.value
										? rideSharingOptions.filter((option) =>
												field.value.includes(option.id)
										  )
										: []
								}
							/>
						)}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}
